import React from "react"
import PropTypes from "prop-types"
import { Button as ReactStrapButton, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import cloneDeep from "lodash/cloneDeep"
import { translate } from "react-i18next"
import { connect } from "react-redux"
import { compose } from "redux"
import styled from "styled-components"
import { Button } from "../../themed-html-components"
import CustomReportReviewDetails from "./QuestionSelection/CustomReportReviewDetails"
import CustomReportSelectedQuestions from "./QuestionSelection/CustomReportSelectedQuestions"
import { ExportDetailsShape, WorkflowStepShape } from "../../../state/shapes/CustomReportShapes"

const IntroArea = styled.div`
  margin-bottom: 1em;
`

const WarningArea = styled.p`
  border: 1px solid grey;
  border-radius: 5px;
  text-align: center;
  background: red;
  color: white;
  margin-top: 15px;
  margin-bottom: 0;
`

const bigModal = {
  maxWidth: "1200px"
}

class QuestionSelectionModal extends React.Component {
  constructor(props) {
    super(props)

    this.flattenQuestions = () => {
      return this.props.reviewData
        .reduce((arr, page) => {
          return arr.concat(page.pages)
        }, [])
        .reduce((newArr, data) => {
          return newArr.concat(data.questions)
        }, [])
    }

    this.state = {
      activeFormTab: this.props.reviewData[0].clientWorkflowStepId,
      activeSelectionTab: 1,
      exportTabs: cloneDeep(this.props.exportDetails),
      flatQuestions: this.flattenQuestions(),
      showWarning: false
    }
  }
  closeModal = () => {
    this.props.closePopup()
  }

  submitChanges = () => {
    this.props.updateSelectedQuestions(this.state.exportTabs)
    this.closeModal()
  }

  tabClicked = tab => {
    this.setState({ activeFormTab: tab.id })
  }

  selectionTabClicked = tab => {
    if (this.findTab(tab) >= 0) {
      this.setState({ activeSelectionTab: tab.id })
    }
  }

  questionAdded = (selectedQuestionId, currentTab) => {
    for (let i = 0; i < currentTab.selectedQuestions.length; i++) {
      const q = currentTab.selectedQuestions[i]

      if (q.questionId === selectedQuestionId) {
        this.setState({ showWarning: true })
        return true
      }
    }

    return false
  }

  addQuestion = (selectedQuestionId, clientWorkflowStepId, tabId) => {
    const newExport = this.state.exportTabs
    const currentTab = newExport.find(tabObject => {
      return tabObject.id === tabId
    })

    if (!this.questionAdded(selectedQuestionId, currentTab)) {
      const requiredQuestion = this.state.flatQuestions.find(question => {
        return (
          question.questionId === selectedQuestionId &&
          question.clientWorkflowStepId === clientWorkflowStepId
        )
      })

      currentTab.selectedQuestions = [...currentTab.selectedQuestions, requiredQuestion]

      this.setState({ exportTabs: newExport })
    }
  }

  questionSelected = (selectedQuestionId, clientWorkflowStepId, tab) => {
    let tabId = tab
    if (!tabId) {
      // If no tab is specified, add to the current active tab
      tabId = this.state.activeSelectionTab
    }

    this.addQuestion(selectedQuestionId, clientWorkflowStepId, tabId)
  }

  questionRemoved = (selectedQuestionId, tab) => {
    const newExport = this.state.exportTabs

    const currentTab = newExport.find(tabObject => {
      return tabObject.id === tab
    })

    currentTab.selectedQuestions = currentTab.selectedQuestions.filter(question => {
      return question.questionId !== selectedQuestionId
    })

    this.setState({ exportTabs: newExport })
  }

  findTab = tab => {
    let index = -1
    for (let i = 0; i < this.state.exportTabs.length; i++) {
      if (this.state.exportTabs[i].id === tab.id) index = i
    }

    return index
  }

  updateTab = tab => {
    const index = this.findTab(tab)

    if (index > -1) {
      const editExportTabs = this.state.exportTabs
      editExportTabs[index].name = tab.name

      this.setState({
        exportTabs: editExportTabs
      })
    }
  }

  deleteTab = tab => {
    const editExportTabs = this.state.exportTabs

    if (window.confirm(`Delete ${this.props.t(tab.name)}?`)) {
      const index = this.findTab(tab)

      if (index > -1) {
        editExportTabs.splice(index, 1)

        this.setState({
          exportTabs: editExportTabs
        })

        // Select the previous tab, or if it's the first tab being deleted
        // then select the next tab
        const tabIndexToSelect = Math.max(index - 1, 0)
        this.selectionTabClicked(editExportTabs[tabIndexToSelect])
      }
    }
  }

  createNewTab = () => {
    // Get the highest id of the existing sheets to make our new id
    const highestId = Math.max(
      ...this.state.exportTabs.map(o => {
        return o.id
      })
    )
    const newTabId = highestId + 1
    // Create a default name for the sheet
    const newSheetName = `${this.props.t("CustomReport.Common.DefaultNamePrefix")} ${newTabId}`
    this.setState({
      exportTabs: [
        ...this.state.exportTabs,
        {
          id: newTabId,
          name: newSheetName,
          selectedQuestions: []
        }
      ],
      activeSelectionTab: newTabId
    })
  }
  clearCheckboxes = () => {
    // Need code here to loop around all the pages/groups/questions and add a 'checked.
    this.props.reviewData.map(function(clientWorkflowStep) {
      clientWorkflowStep.pages.map(function(page) {
        page.checked = false
        page.questions.map(function(question) {
          question.checked = false
        })
      })
    })
  }
  resetWarningMessage = () => {
    this.setState({ showWarning: false })
  }
  render() {
    const { t, reviewData } = this.props
    this.clearCheckboxes()

    return (
      <div>
        <Modal isOpen toggle={this.closeModal} className="modal-lg" style={bigModal}>
          <ModalHeader toggle={this.closeModal}>
            {t("CustomReport.QuestionSelection.ModalTitle")}
          </ModalHeader>
          <ModalBody>
            <IntroArea>
              <em>{t("CustomReport.QuestionSelection.ModalIntro")}</em>
            </IntroArea>
            <div className="row">
              <div className="col-md-6">
                <CustomReportReviewDetails
                  reviewData={reviewData}
                  tabClicked={this.tabClicked}
                  activeTab={this.state.activeFormTab}
                  questionSelected={this.questionSelected}
                  clearCheckboxes={this.clearCheckboxes}
                  resetWarningMessage={this.resetWarningMessage}
                />
              </div>
              <div className="col-md-6">
                <CustomReportSelectedQuestions
                  exportTabs={this.state.exportTabs}
                  tabClicked={this.selectionTabClicked}
                  activeTab={this.state.activeSelectionTab}
                  questionSelected={this.questionSelected}
                  questionRemoved={this.questionRemoved}
                  createNewTab={this.createNewTab}
                  deleteTab={this.deleteTab}
                  updateTab={this.updateTab}
                />
              </div>
            </div>
            {this.state.showWarning && (
              <div className="row">
                <div className="col-md-12">
                  <WarningArea>
                    <i className="fa fa-exclamation-circle" style={{ paddingRight: "10px" }} />{" "}
                    {t("Common.QuestionSelectionWarning")}
                  </WarningArea>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button inverted onClick={this.submitChanges}>
              {t("Common.Submit")}
            </Button>{" "}
            <ReactStrapButton color="secondary" onClick={this.closeModal}>
              {t("Common.Cancel")}
            </ReactStrapButton>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
}
QuestionSelectionModal.propTypes = {
  t: PropTypes.func.isRequired,
  closePopup: PropTypes.func.isRequired,
  reviewData: PropTypes.arrayOf(PropTypes.shape(WorkflowStepShape)).isRequired,
  updateSelectedQuestions: PropTypes.func.isRequired,
  exportDetails: PropTypes.arrayOf(PropTypes.shape(ExportDetailsShape)).isRequired
}

function mapStateToProps(state) {
  return {
    theme: state.userDetail.theme
  }
}

export default compose(translate(), connect(mapStateToProps))(QuestionSelectionModal)



// WEBPACK FOOTER //
// ./src/components/custom-report/Modals/QuestionSelectionModal.jsx